import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CompassIcon, SearchCheckIcon, PaletteIcon, CodeIcon, RocketIcon, LineChartIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const STEPS = [{
  icon: CompassIcon,
  title: 'Discovery',
  description: 'We gather insights on your business, audience, goals, and challenges, then build a custom website strategy around your objectives.'
}, {
  icon: SearchCheckIcon,
  title: 'Research',
  description: 'We analyze your current site, industry trends, and competitors to find the features and SEO opportunities that give your website a competitive edge.'
}, {
  icon: PaletteIcon,
  title: 'Design',
  description: 'Our team crafts a unique design that reflects your brand while prioritizing performance, mobile responsiveness, and accessibility.'
}, {
  icon: CodeIcon,
  title: 'Development',
  description: "From essential SEO elements to ADA compliance, we build a website that's both beautiful and functional."
}, {
  icon: RocketIcon,
  title: 'Launch',
  description: 'We launch your new WordPress website with a seamless transition, so it performs from day one.'
}, {
  icon: LineChartIcon,
  title: 'Measurement',
  description: 'Post-launch, we track performance with analytics and user feedback to measure success, monitor SEO progress, and find further improvements.'
}];
export function ApproachSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const goTo = (index: number) => setActiveIndex((index + STEPS.length) % STEPS.length);
  const step = STEPS[activeIndex];
  return <section className="w-full bg-white" data-id="element-7" data-mp="2nbu8zsvpt8zo">
      <div className="mx-auto w-full max-w-4xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8" data-id="element-8" data-mp="2nasm8r5c6y24">
        <h2 className="font-display text-center text-2xl font-bold text-[var(--color-navy)] sm:text-3xl" data-id="element-9" data-mp="2reqkz0qr26un">
          Our Approach to Senior Living Websites
        </h2>
        <div className="mt-12 flex items-center justify-center gap-4 sm:gap-8" data-id="element-10" data-mp="6n6ww6o50hdp">
          <button type="button" onClick={() => goTo(activeIndex - 1)} aria-label="Previous step" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-[var(--color-navy)] transition-colors hover:bg-slate-100" data-id="element-11" data-mp="23ocghn4e0tb9">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-12" data-mp="2co98nypzkab2" />
          </button>
          <div className="relative min-h-[260px] w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center shadow-md sm:p-10" data-id="element-13" data-mp="27qh86gb9zfv9">
            <AnimatePresence mode="wait" data-id="element-14" data-mp="1fzktrb9kh35v">
              <motion.div key={step.title} initial={{
              opacity: 0,
              x: 24
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -24
            }} transition={{
              duration: 0.35,
              ease: 'easeOut'
            }} data-id="element-15" data-mp="1g0mgiczy3e3f">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-navy)]" data-id="element-16" data-mp="wbc1z2bxqz6w">
                  <step.icon className="h-7 w-7 text-[var(--color-lime)]" aria-hidden="true" data-id="element-17" data-mp="37xcj7c9g894j" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-[var(--color-lime-dark)]" data-id="element-18" data-mp="3cqfebotvjqmu">
                  Step {activeIndex + 1} of {STEPS.length}
                </span>
                <h3 className="font-display mt-2 text-xl font-bold text-[var(--color-navy)]" data-id="element-19" data-mp="33xc37n6pbpvd">
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base" data-id="element-20" data-mp="3vnqbn3vy49zn">
                  {step.description}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
          <button type="button" onClick={() => goTo(activeIndex + 1)} aria-label="Next step" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-[var(--color-navy)] transition-colors hover:bg-slate-100" data-id="element-21" data-mp="3mmirdpq9l9ok">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-22" data-mp="2b5ipq3i11ihv" />
          </button>
        </div>
        <div className="mt-6 flex items-center justify-center gap-2" role="tablist" aria-label="Approach steps" data-id="element-23" data-mp="2qjd1ch69tw1w">
          {STEPS.map((s, i) => <button key={s.title} type="button" role="tab" aria-selected={i === activeIndex} aria-label={s.title} onClick={() => setActiveIndex(i)} className={`h-2.5 rounded-full transition-all ${i === activeIndex ? 'w-7 bg-[var(--color-navy)]' : 'w-2.5 bg-slate-300 hover:bg-slate-400'}`} data-id="element-24" data-mp="2hof3qws6hv48" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}